/**
 * Get value for current breakpoint from responsive styles object
 * as a prop in components that use this hook.
 *
 * @example
 * paddingBottom={{ default: '150px', m: '50px' }}
 * // currentBreakpoint is 'sm'
 * // paddingBottom = '50px'
 */
import { AtLeastOne } from '../types/Utils';
import {
  breakpointsKeysWithDefault,
  BreakpointWithDefault,
  getBreakpointIndex,
  useBreakpoint,
} from './useBreakpoint';

export type StyledSystemStyleObj = AtLeastOne<
  Record<BreakpointWithDefault, string>
>;

export type StyledSystemStyle = string | StyledSystemStyleObj;
export type StyledSystemStyles = Record<string, StyledSystemStyle>;

/**
 * Utils
 */
function getStyleFromObject(
  styleObj: StyledSystemStyleObj,
  currentBreakpoint: BreakpointWithDefault,
) {
  const styleObjKeys = Object.keys(styleObj) as BreakpointWithDefault[];
  const currentBreakpointIndex = getBreakpointIndex(currentBreakpoint);

  const currentStyledBreakpoint = breakpointsKeysWithDefault.find(
    (breakpointKey) => {
      const breakpointKeyIndex = getBreakpointIndex(breakpointKey);
      const closestBreakpoint = styleObjKeys.includes(breakpointKey);
      if (currentBreakpointIndex <= breakpointKeyIndex) {
        return closestBreakpoint;
      } else {
        return false;
      }
    },
  );

  return styleObj[currentStyledBreakpoint || 'default'] || null;
}

function getStyles(
  styles: StyledSystemStyles,
  currentBreakpoint: BreakpointWithDefault,
) {
  return Object.entries(styles).reduce(
    (stylesObj: StyledSystemStyles, [styleKey, styleValue]) => {
      if (!styleValue) return stylesObj;

      if (typeof styleValue === 'string') {
        stylesObj[styleKey] = styleValue;
      } else if (typeof styleValue === 'object') {
        const styleFromObject = getStyleFromObject(
          styleValue,
          currentBreakpoint,
        );
        if (styleFromObject) stylesObj[styleKey] = styleFromObject;
      }

      return stylesObj;
    },
    {},
  );
}

/**
 * Hook
 */
export default function useStyles(styles: StyledSystemStyles) {
  const currentBreakpoint = useBreakpoint();
  const computedStyles = getStyles(styles, currentBreakpoint);

  return computedStyles;
}
